import { describeCaughtError } from "#lib/utils/caught-error.js";

/**
 * Client error hook. Uncaught errors during client-side navigation / rendering
 * (SvelteKit calls this) were previously invisible in PostHog. Forward them to
 * PostHog Error Tracking when the SDK is loaded.
 *
 * `capture_exceptions: true` (posthog.init) covers unhandled window errors; this
 * hook covers SvelteKit's framework-level error path and tags it with the route.
 *
 * SvelteKit 3 passes `{ kind, error, event }` (no `status` / `message` outside
 * dev); describeCaughtError reads that and reports the same set as the server
 * hook: unknown errors and anything >= 500, not 404s.
 *
 * Guards on window.posthog and never throws - an error reporter that throws
 * would replace one error with another. Returns nothing, so SvelteKit keeps the
 * caught error's own status and message.
 *
 * @type {import('@sveltejs/kit/hooks').HandleClientError}
 */
export function handleError({ kind, error, event }) {
  const { status, message, report } = describeCaughtError({ kind, error });
  if (!report) return;
  try {
    if (
      typeof window !== "undefined" &&
      window.posthog &&
      typeof window.posthog.captureException === "function"
    ) {
      const err =
        kind === "unknown"
          ? error instanceof Error
            ? error
            : new Error(String(error))
          : new Error(message);
      window.posthog.captureException(err, {
        $exception_source: "sveltekit-client",
        route: event?.route?.id ?? null,
        path:
          typeof window !== "undefined" && window.location
            ? window.location.pathname
            : null,
        status,
      });
    }
  } catch {
    /* never let error reporting throw */
  }
}
